/**
 * Cloud Operations — Overview sub-page.
 *
 * Renders the KPI grid + recent system-workflow-runs strip + surface roadmap.
 * Mounts at /cloud-operations/overview under OperatorsConsoleLayout.
 *
 * Companion docs:
 *   /agentlets-production/Prove7_Operators_Console_Design.docx §5 (API surface)
 *   /agentlets-production/Prove7_Operators_Console_Mockup.html (visual reference)
 */

import React, { useEffect, useState } from 'react';
import { getOperatorsSummary } from '../../services/cloudOperationsApi';

const TINTS = {
  ink:        '#0a1d44',
  text:       '#1a1f2b',
  text2:      '#5b6b7e',
  text3:      '#8896a8',
  stroke:     '#d4dbe5',
  stroke2:    '#e8edf3',
  bgCard:     '#ffffff',
  bgTint:     '#f7f9fc',
  bgAlt:      '#eef2f8',
  success:    '#2e7d4f',
  successBg:  '#e7f4ec',
  warn:       '#c58b00',
  warnBg:     '#fff4d9',
  danger:     '#c0392b',
  dangerBg:   '#fbe9e7',
  info:       '#1f5fa6',
  infoBg:     '#e8f0fb',
};

export default function OverviewPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getOperatorsSummary()
      .then((d) => { if (alive) { setSummary(d); setLoading(false); } })
      .catch((e) => { if (alive) { setError(e?.message || String(e)); setLoading(false); } });
    const t = setInterval(() => {
      getOperatorsSummary().then(setSummary).catch(() => {});
    }, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div>
      {loading && <Banner color={TINTS.info} bg={TINTS.infoBg}>Loading Cloud Operations summary…</Banner>}
      {error   && <Banner color={TINTS.danger} bg={TINTS.dangerBg}>Could not load summary: {error}</Banner>}
      {summary && <SummaryBlock summary={summary} />}
      <SurfaceRoadmap />
    </div>
  );
}

function Banner({ color, bg, children }) {
  return (
    <div style={{
      background: bg, borderLeft: `3px solid ${color}`, padding: '12px 16px',
      borderRadius: 4, color, fontSize: 13, marginBottom: 20,
    }}>{children}</div>
  );
}

function SummaryBlock({ summary }) {
  const kpi = summary?.kpi || {};
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <Kpi label="Active deployments" value={kpi.active_deployments ?? '—'}
             delta={`${kpi.hosted_count ?? 0} Hosted · ${kpi.self_hosted_count ?? 0} Self-Hosted`} />
        <Kpi label="SLO posture"
             value={`${kpi.slo_posture_meeting ?? '—'} / ${kpi.slo_posture_total ?? '—'}`}
             delta="meeting target (rolling 30 d)" />
        <Kpi label="Monthly spend (Hosted)"
             value={kpi.monthly_spend_usd_hosted != null
               ? `$ ${kpi.monthly_spend_usd_hosted.toLocaleString()}` : '—'}
             delta={kpi.monthly_spend_forecast_usd
               ? `forecast $ ${kpi.monthly_spend_forecast_usd.toLocaleString()}` : '—'} />
        <Kpi label="Runs in progress" value={kpi.open_incidents ?? '—'}
             delta="System Deployment Workflows currently executing" />
      </div>
      <RecentRuns runs={summary?.recent_runs || []} />
    </>
  );
}

function Kpi({ label, value, delta }) {
  return (
    <div style={{
      background: TINTS.bgCard, border: `1px solid ${TINTS.stroke}`,
      borderRadius: 6, padding: '16px 20px',
    }}>
      <div style={{
        fontSize: 11, textTransform: 'uppercase', color: TINTS.text3,
        letterSpacing: '.08em', fontWeight: 600, marginBottom: 4,
      }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: TINTS.ink, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 12, color: TINTS.text2, marginTop: 4 }}>{delta}</div>
    </div>
  );
}

function RecentRuns({ runs }) {
  return (
    <div style={{
      background: TINTS.bgCard, border: `1px solid ${TINTS.stroke}`, borderRadius: 6,
    }}>
      <div style={{
        padding: '16px 20px', borderBottom: `1px solid ${TINTS.stroke2}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <h3 style={{ margin: 0, fontSize: 15, color: TINTS.ink, fontWeight: 600 }}>
          Recent system workflow runs
        </h3>
        <span style={{ color: TINTS.text2, fontSize: 12, marginLeft: 12 }}>
          Showing {runs.length} most recent
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: TINTS.bgTint }}>
            <Th>Run</Th><Th>Workflow</Th><Th>Target</Th>
            <Th>Initiated by</Th><Th>Progress</Th><Th>Result</Th><Th>Started</Th>
          </tr>
        </thead>
        <tbody>
          {runs.length === 0 ? (
            <tr><td colSpan={7} style={{
              padding: 32, textAlign: 'center', color: TINTS.text3, fontStyle: 'italic',
            }}>
              No system workflow runs yet. Once a System Deployment Workflow has
              executed against a target, runs will appear here.
            </td></tr>
          ) : runs.map((r) => (
            <tr key={r.run_id} style={{ borderTop: `1px solid ${TINTS.stroke2}` }}>
              <Td><code style={{ fontSize: 11 }}>{shortId(r.run_id)}</code></Td>
              <Td><code style={{ fontSize: 11 }}>{shortId(r.workflow_id)}</code>
                  <div style={{ color: TINTS.text3, fontSize: 11 }}>{r.workflow_version}</div></Td>
              <Td><code style={{ fontSize: 11 }}>{shortId(r.target_id)}</code></Td>
              <Td><code style={{ fontSize: 11 }}>{shortId(r.initiated_by)}</code></Td>
              <Td>{r.step_progress}</Td>
              <Td>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <ResultPill result={r.result} />
                  {r.dry_run ? <DryRunPill /> : null}
                </span>
              </Td>
              <Td>{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }) {
  return <th style={{
    textAlign: 'left', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase',
    letterSpacing: '.06em', color: TINTS.text2, fontWeight: 600,
    borderBottom: `1px solid ${TINTS.stroke}`,
  }}>{children}</th>;
}
function Td({ children }) {
  return <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>{children}</td>;
}

function ResultPill({ result }) {
  const map = {
    in_progress: { fg: TINTS.info,    bg: TINTS.infoBg,    label: 'In progress' },
    success:     { fg: TINTS.success, bg: TINTS.successBg, label: 'Success'     },
    failed:      { fg: TINTS.danger,  bg: TINTS.dangerBg,  label: 'Failed'      },
    cancelled:   { fg: TINTS.text2,   bg: '#eef2f8',       label: 'Cancelled'   },
    timed_out:   { fg: TINTS.warn,    bg: TINTS.warnBg,    label: 'Timed out'   },
  };
  const c = map[result] || { fg: TINTS.text2, bg: '#eef2f8', label: result || '—' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 600, color: c.fg, background: c.bg,
      border: `1px solid ${c.fg}33`,
    }}>{c.label}</span>
  );
}

/**
 * A completed dry run carries result='success' too — every GCP/helm connector
 * skill short-circuits into its dry-run branch when
 * prove7.gcp.live-execution.enabled=false and returns SYNTHETIC outputs, so
 * NOTHING was provisioned. Showing "Success" alone would be a false green on
 * the most consequential action the platform takes, so we always render this
 * alongside the result.
 */
function DryRunPill() {
  return (
    <span
      title="Simulated run — live execution was disabled, so no cloud resources were created."
      style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 999,
        fontSize: 10, fontWeight: 700, letterSpacing: '.04em',
        color: TINTS.warn, background: TINTS.warnBg,
        border: `1px solid ${TINTS.warn}55`, textTransform: 'uppercase',
      }}
    >
      Dry run
    </span>
  );
}

function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length <= 12 ? s : s.slice(0, 8) + '…' + s.slice(-4);
}

function SurfaceRoadmap() {
  const surfaces = [
    { name: 'Overview',                state: 'shipped',     tag: 'v0.1' },
    { name: 'Cloud accounts',          state: 'shipped',     tag: 'W2'   },
    { name: 'System workflows',        state: 'shipped',     tag: 'W2'   },
    { name: 'Deployment targets',      state: 'shipped',     tag: 'W2'   },
    { name: 'Deployments list',        state: 'in-progress', tag: 'W2.6' },
    { name: 'Deployment detail',       state: 'in-progress', tag: 'W2.6' },
    { name: 'New deployment wizard',   state: 'in-progress', tag: 'W2.6' },
    { name: 'Workflow canvas (read)',  state: 'in-progress', tag: 'W2.7' },
    { name: 'Bridge agents',           state: 'planned',     tag: 'W2.6' },
    { name: 'SLA matrix',              state: 'planned',     tag: 'W2.6' },
    { name: 'Operations log',          state: 'planned',     tag: 'W2.6' },
  ];
  return (
    <div style={{ marginTop: 32 }}>
      <h3 style={{ fontSize: 15, color: TINTS.ink, fontWeight: 600, margin: '0 0 12px' }}>
        Surface roadmap
      </h3>
      <p style={{ fontSize: 12, color: TINTS.text2, margin: '0 0 12px' }}>
        Per the Cloud Operations (formerly Operators Console) design doc Wave 2 plan. Surfaces marked
        "shipped" are functional in this build; "in-progress" surfaces are
        navigable but show placeholder content.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {surfaces.map((s) => {
          const color = s.state === 'shipped'     ? TINTS.success
                      : s.state === 'in-progress' ? TINTS.info
                      :                              TINTS.text3;
          const bg    = s.state === 'shipped'     ? TINTS.successBg
                      : s.state === 'in-progress' ? TINTS.infoBg
                      :                              '#eef2f8';
          return (
            <div key={s.name} style={{
              background: bg, border: `1px solid ${color}33`, borderRadius: 4,
              padding: '8px 12px', fontSize: 12,
            }}>
              <div style={{ color, fontWeight: 600 }}>{s.name}</div>
              <div style={{ color: TINTS.text2, fontSize: 11 }}>{s.state} · {s.tag}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
